import { TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useCardsOnSale } from '@sorare/core/src/hooks/search/useCardsOnSale';

import { CollectionUpsellCard } from 'components/card/CollectionUpsellCard';
import { useCart } from 'hooks/useCart';

import type {
  MissingCardsForCollectionQuery,
  MissingCardsForCollectionQueryVariables,
  MissingCardsForCollection_cardCollection,
  MissingCardsForCollection_userCardCollection,
} from './__generated__/index.graphql';
import { useSortPrimaryBuys } from './useSortPrimaryBuys';

const Root = styled.div`
  position: relative;
  isolation: isolate;

  &::before {
    content: '';
    z-index: 1;
    position: absolute;
    left: 0;
    top: 0;
    width: var(--double-unit);
    height: 100%;
    background: linear-gradient(to right, var(--c-black) 0%, transparent 100%);
  }
  &::after {
    content: '';
    z-index: 1;
    position: absolute;
    right: 0;
    top: 0;
    width: var(--double-unit);
    height: 100%;
    background: linear-gradient(
      to left,
      var(--background-color) 0%,
      var(--background-color) 100%,
      transparent 100%
    );
    background: linear-gradient(to left, var(--c-black) 0%, transparent 100%);
  }
`;
const CollectionUpsellCards = styled(Horizontal)`
  overflow-x: auto;
  padding: var(--double-unit);
`;

const CollectionUpsellSkeleton = styled(Skeleton)`
  width: 174px;
  flex-shrink: 0;
  min-height: 360px;
  border-radius: var(--unit);
`;

const MISSING_CARDS_FOR_COLLECTION_QUERY = gql`
  query MissingCardsForCollectionQuery($slugs: [String!]) {
    anyCards(slugs: $slugs) {
      slug
      ...CollectionUpsellCard_anyCard
      ...useSortPrimaryBuys_anyCard
    }
  }
  ${CollectionUpsellCard.fragments.anyCard}
  ${useSortPrimaryBuys.fragments.anyCard}
` as TypedDocumentNode<
  MissingCardsForCollectionQuery,
  MissingCardsForCollectionQueryVariables
>;

const OPTIONAL_FILTERS = ['sale.type:PrimaryOffer'];

type Props = {
  cardCollection: Nullable<MissingCardsForCollection_cardCollection>;
  userCardCollection: Nullable<MissingCardsForCollection_userCardCollection>;
};

export const MissingCardsForCollection = ({
  cardCollection,
  userCardCollection,
}: Props) => {
  const { cart } = useCart();
  const alreadyOwnedPlayerSlugs =
    (userCardCollection?.cardCollectionCards || [])?.map(
      c => c.anyCard.anyPlayer.slug
    ) || [];

  const alreadyInCartPlayerSlugs =
    cart?.mainCartItems
      ?.filter(ci => ci.card.rarityTyped === cardCollection?.rarity)
      .map(i => i.card.anyPlayer.slug) || [];

  const excludedPlayerSlugs = [
    ...alreadyOwnedPlayerSlugs,
    ...alreadyInCartPlayerSlugs,
  ];
  const { slugs } = useCardsOnSale({
    index: 'Cards On Sale Lowest Price',
    distinct: true,
    rarities: [cardCollection?.rarity || Rarity.limited],
    teamSlug: cardCollection?.anyTeam?.slug,
    season: cardCollection?.season?.startYear,
    optionalFilters: OPTIONAL_FILTERS,
    primaryOffer: true,
    ...(excludedPlayerSlugs?.length && { excludedPlayerSlugs }),
    skip: !cardCollection,
  });

  const { data, loading: gqlLoading } = useQuery(
    MISSING_CARDS_FOR_COLLECTION_QUERY,
    {
      variables: { slugs },
      skip: !slugs.length,
    }
  );

  const sortedCardsByPrice = useSortPrimaryBuys({
    anyCards: data?.anyCards || [],
  });

  return (
    <Root>
      <CollectionUpsellCards gap={2}>
        {gqlLoading && slugs.map(s => <CollectionUpsellSkeleton key={s} />)}
        {sortedCardsByPrice.map(anyCard => {
          return <CollectionUpsellCard key={anyCard.slug} anyCard={anyCard} />;
        })}
      </CollectionUpsellCards>
    </Root>
  );
};

MissingCardsForCollection.fragments = {
  cardCollection: gql`
    fragment MissingCardsForCollection_cardCollection on CardCollection {
      slug
      anyTeam {
        slug
      }
      rarity
      season {
        startYear
      }
    }
  ` as TypedDocumentNode<MissingCardsForCollection_cardCollection>,
  userCardCollection: gql`
    fragment MissingCardsForCollection_userCardCollection on UserCardCollection {
      id
      cardCollectionCards {
        id
        anyCard {
          slug
          anyPlayer {
            slug
          }
        }
      }
    }
  ` as TypedDocumentNode<MissingCardsForCollection_userCardCollection>,
};
